import { useMemo } from 'react';
import type { MessagePart, ProcessedPart } from '~/components/chat/assistant-message/types';
import {
  isToolInvocationPart,
  isToolInvocationGroupPart,
  type ToolInvocationGroupUIPart,
  isTodoWritePart,
} from '~/lib/ai';
import { nonNullable } from '~/lib/filters';
import { CC_MAGIC_STRING, SUPABASE_DATABASE_REGEX } from '~/utils/constants';
import { BOLT_QUICK_ACTIONS_REGEX } from '~/utils/quick-actions';

export function usePartsWithId(parts?: MessagePart[]): ProcessedPart[] {
  return useMemo(() => {
    if (!parts || parts.length === 0) {
      return [];
    }

    return parts
      .map((part) => {
        switch (part.type) {
          case 'text': {
            const cleanedText = part.text
              .replace(BOLT_QUICK_ACTIONS_REGEX, '')
              .replace(CC_MAGIC_STRING, '')
              .replace(/\n{3,}/g, '\n\n') // replace 3+ newlines with 2
              .replace(SUPABASE_DATABASE_REGEX, 'Bolt Database')
              .trim();

            if (!cleanedText.trim()) {
              return null;
            }

            return { ...part, text: cleanedText, id: part.text };
          }

          case 'tool-invocation': {
            return { ...part, id: part.toolInvocation.toolCallId };
          }

          default: {
            return null;
          }
        }
      })
      .filter(nonNullable);
  }, [parts]);
}

export function useProcessedParts(parts?: MessagePart[]): ProcessedPart[] {
  const partsWithId = usePartsWithId(parts);

  return useMemo(() => {
    if (!parts || parts.length === 0) {
      return [];
    }

    // group consecutive tool invocations (2+ items, excluding TodoWrite)
    return partsWithId.reduce<((typeof partsWithId)[number] | (ToolInvocationGroupUIPart & { id: string }))[]>(
      (acc, part, index) => {
        if (isToolInvocationPart(part) && part.toolInvocation.toolName !== 'TodoWrite') {
          const prevPart = acc[acc.length - 1];
          const nextPart = partsWithId[index + 1];

          const shouldStartGroup = nextPart && isToolInvocationPart(nextPart) && !isTodoWritePart(nextPart);

          const canAddToGroup = prevPart && isToolInvocationGroupPart(prevPart);

          if (canAddToGroup) {
            prevPart.toolInvocations.push(part.toolInvocation);
          } else if (shouldStartGroup) {
            acc.push({
              id: part.toolInvocation.toolCallId,
              type: 'tool-invocation-group',
              toolInvocations: [part.toolInvocation],
            });
          } else {
            acc.push(part);
          }
        } else {
          acc.push(part);
        }

        return acc;
      },
      [],
    );
  }, [partsWithId]);
}
